"use client";

import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, GripVertical, ListOrdered, RotateCcw, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import ActivityFrame from "./ActivityFrame";

export interface SequencingStep {
  id: string;
  text: string;
}

function shuffled<T>(items: T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Steps arrive from the server ALREADY SHUFFLED with opaque ids — the correct
 * order never reaches the client. Checking posts the arrangement to
 * /check-sequencing, which returns per-position correctness.
 */
export default function SequencingActivity({
  courseId,
  interactiveId,
  title,
  prompt,
  steps,
  onComplete,
  check: checkOverride,
}: {
  courseId?: string;
  interactiveId?: string;
  title: string;
  prompt?: string;
  steps: SequencingStep[];
  // Called once when the steps are arranged correctly
  onComplete?: () => void;
  // Editor preview of UNSAVED drafts supplies a local checker (the
  // instructor already knows the answers); students always hit the server.
  check?: (order: string[]) => Promise<{ positions: boolean[]; all_correct: boolean }>;
}) {
  const [order, setOrder] = useState<string[]>(() => steps.map((s) => s.id));
  const textById = useMemo(() => new Map(steps.map((s) => [s.id, s.text])), [steps]);
  // null = unchecked; otherwise per-position correctness from the server
  const [positions, setPositions] = useState<boolean[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(false);
  const completed = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const allCorrect = positions !== null && positions.every(Boolean);
  const correctCount = positions?.filter(Boolean).length ?? 0;

  if (steps.length === 0) return null;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setPositions(null);
    setCheckError(false);
    setOrder((prev) =>
      arrayMove(prev, prev.indexOf(String(active.id)), prev.indexOf(String(over.id))),
    );
  };

  const check = async () => {
    setChecking(true);
    setCheckError(false);
    try {
      const result = checkOverride
        ? await checkOverride(order)
        : await api<{ positions: boolean[]; all_correct: boolean }>(
            `/api/v1/courses/${courseId}/interactives/${interactiveId}/check-sequencing`,
            { method: "POST", body: JSON.stringify({ order }) },
          );
      setPositions(result.positions);
      if (result.all_correct && !completed.current) {
        completed.current = true;
        onComplete?.();
      }
    } catch {
      setCheckError(true);
    } finally {
      setChecking(false);
    }
  };

  const reset = () => {
    setPositions(null);
    setCheckError(false);
    setOrder(shuffled(steps.map((s) => s.id)));
  };

  const checked = positions !== null;

  return (
    <ActivityFrame
      type="sequencing"
      kindLabel="Ordering"
      icon={ListOrdered}
      title={title}
      meta={
        checked ? (
          <span className="activity__done" role="status">
            <Check className="h-3.5 w-3.5" />
            {allCorrect ? "Correct!" : `${correctCount} / ${steps.length} in place`}
          </span>
        ) : undefined
      }
    >
      {prompt && <p className="mb-2.5 text-sm text-muted">{prompt}</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ol className="space-y-2">
            {order.map((id, pos) => (
              <SortableStep
                key={id}
                id={id}
                position={pos}
                text={textById.get(id) ?? ""}
                state={checked ? (positions[pos] ? "is-correct" : "is-wrong") : ""}
                verdict={checked ? positions[pos] : null}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>

      <div className="mt-3 flex items-center justify-between">
        {checkError ? (
          <span className="text-xs text-danger" role="status">
            Couldn&apos;t check right now — try again.
          </span>
        ) : checked && !allCorrect ? (
          <button onClick={reset} className="activity__btn flex items-center gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" /> Shuffle &amp; retry
          </button>
        ) : (
          <span className="text-xs text-faint">
            {checked ? "Nicely ordered." : "Drag the handles to arrange the steps, then check."}
          </span>
        )}
        {!(checked && allCorrect) && (
          <button
            onClick={check}
            disabled={checking}
            className="rounded-md px-4 py-1.5 text-sm font-medium text-white transition-opacity disabled:opacity-50"
            style={{ background: "var(--la)" }}
          >
            {checking ? "Checking…" : "Check order"}
          </button>
        )}
      </div>
    </ActivityFrame>
  );
}

function SortableStep({
  id,
  position,
  text,
  state,
  verdict,
}: {
  id: string;
  position: number;
  text: string;
  state: string;
  verdict: boolean | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`seq-step ${state} ${isDragging ? "z-10 opacity-70 shadow-lg" : ""}`}
    >
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--la-soft)] text-[11px] font-bold text-[var(--la-ink)]">
        {position + 1}
      </span>
      <span className="flex-1">{text}</span>
      {/* Icon + text, never color alone */}
      {verdict === true && (
        <span className="inline-flex items-center text-success" aria-label="In the right place">
          <Check className="h-4 w-4" />
        </span>
      )}
      {verdict === false && (
        <span className="inline-flex items-center text-danger" aria-label="Not in the right place">
          <X className="h-4 w-4" />
        </span>
      )}
      <button
        type="button"
        aria-label={`Reorder "${text}"`}
        className="shrink-0 cursor-grab touch-none rounded p-1 text-faint transition-colors hover:bg-line/50 hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
    </li>
  );
}
